import { Badge } from "@/components/ui/badge";
import { Award, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { cn, sizes, spacing } from "@/utilities/tailwind-utils";
import type { DeclaredPaymentStatus, ShiftFeeStatus } from "@/domain/payments";

export function getEntityStatusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <Badge variant="default" className="bg-green-500">
          Active
        </Badge>
      );
    case "inactive":
      return <Badge variant="secondary">Inactive</Badge>;
    case "suspended":
      return <Badge variant="destructive">Suspended</Badge>;
    case "banned":
      return <Badge variant="destructive">Banned</Badge>;
    case "pending":
      return (
        <Badge variant="secondary">
          <Clock className={cn(sizes.size3, spacing.mr1)} />
          Pending
        </Badge>
      );
    case "dormant":
      return <Badge variant="outline">Dormant</Badge>;
    default:
      return <Badge variant="outline">{status || "Unknown"}</Badge>;
  }
}

export function getVerificationBadge(verified: boolean) {
  return verified ? (
    <Badge variant="default" className="bg-blue-500">
      <Award className={cn(sizes.size3, spacing.mr1)} />
      Verified
    </Badge>
  ) : (
    <Badge variant="outline">
      <Clock className={cn(sizes.size3, spacing.mr1)} />
      Pending
    </Badge>
  );
}

export function getDisputeStatusBadge(status: string) {
  switch (status) {
    case "resolved":
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle className={cn(sizes.size3, spacing.mr1)} />
          Resolved
        </Badge>
      );
    // The backend status is "disputed" — "raised" was matched here for years and
    // never sent, so open disputes rendered with the neutral fallback badge.
    case "disputed":
      return (
        <Badge variant="destructive">
          <AlertTriangle className={cn(sizes.size3, spacing.mr1)} />
          Open
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          <Clock className={cn(sizes.size3, spacing.mr1)} />
          {status}
        </Badge>
      );
  }
}

export function getGrowthBadge(growth: number) {
  if (growth > 0) {
    return (
      <Badge variant="default" className="bg-green-500">
        +{growth}%
      </Badge>
    );
  }
  if (growth < 0) {
    return <Badge variant="destructive">{growth}%</Badge>;
  }
  return <Badge variant="outline">0%</Badge>;
}

/**
 * Badge styling for a payment record.
 *
 * `confirmed` is the only state that means the professional has said they were
 * paid, so it is the only one that reads as affirmative. It used to map
 * `captured` / `refunded` — escrow states that no longer exist — which left
 * every real state except `disputed` sharing one neutral badge, and made
 * confirmed indistinguishable from pending at a glance.
 */
export function getPaymentStateBadgeVariant(
  state: DeclaredPaymentStatus | string
): "default" | "secondary" | "destructive" | "outline" {
  switch (state.toLowerCase()) {
    case "confirmed":
      return "default";
    case "disputed":
      return "destructive";
    case "declared":
    case "resolved":
      return "secondary";
    case "pending":
    default:
      return "outline";
  }
}

/** Only a confirmed payment means the professional has said they were paid. */
/**
 * Badge styling for a Tempii shift fee.
 *
 * `failed` is the state needing someone's attention — the practice's card said
 * no and retries may be exhausted — so it is the only destructive one. `settled`
 * is the affirmative; `waived`/`voided` read as neutral history.
 */
export function getFeeStatusBadgeVariant(
  status: ShiftFeeStatus | string
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "settled":
      return "default";
    case "failed":
      return "destructive";
    case "settling":
      return "secondary";
    case "due":
    case "waived":
    case "voided":
    default:
      return "outline";
  }
}

export function isPaymentConfirmed(state: string): boolean {
  return state.toLowerCase() === "confirmed";
}

export function getAdminPaymentStatusBadge(item: {
  status?: string;
  confirmedAt?: string | null;
  declaredAt?: string | null;
}) {
  if (item.status === "disputed") return getEntityStatusBadge("suspended");
  if (item.confirmedAt) return getEntityStatusBadge("active");
  if (item.declaredAt) return getEntityStatusBadge("pending");
  return getEntityStatusBadge("dormant");
}

export function getRoleBadge(role: string) {
  switch (role) {
    case "admin":
      return (
        <Badge variant="default" className="bg-blue-500">
          Admin
        </Badge>
      );
    case "support":
      return (
        <Badge variant="secondary" className="bg-purple-500">
          Support
        </Badge>
      );
    case "practice":
      return <Badge variant="secondary">Practice</Badge>;
    case "professional":
      return (
        <Badge variant="outline" className="border-emerald-600 text-emerald-800">
          Professional
        </Badge>
      );
    case "none":
      return <Badge variant="outline">No role</Badge>;
    default:
      return <Badge variant="outline">User</Badge>;
  }
}
